import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Stack, Typography } from "@mui/material";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { Empty } from "Components/_Shared/Empty";
import { IconBadge } from "Components/_Shared/IconBadge";
import { Loader } from "Components/_Shared/Loader";
import { useGetAnalysis, useGetAnalysisQuestionnaire } from "Infrastructure/ApiZfp/AnalyzerApi";
import { QuestionnaireStatus } from "Infrastructure/ApiZfp/Schema";
import { ANALYZER_EMAIL } from "Infrastructure/Config/constants";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { useLockedInvestmentCaseStatus } from "../_Shared/useLockedInvestmentCaseStatus";
import { QuestionnaireProvider } from "./QuestionnaireContext";
import { QuestionnaireEvaluation } from "./QuestionnaireEvaluation";
import { QuestionnaireForm } from "./QuestionnaireForm";
import { QuestionnaireMoreActions } from "./QuestionnaireMoreActions";
import { isExpiredStatus } from "./utils";

export const QuestionnairePane = () => {
  const { t } = useResource();
  const { versionId, analysisId, readOnly } = useContext(AnalysisDetailContext);

  const { data: analysisData, isPending: isAnalysisPending, isError: isAnalysisError } = useGetAnalysis(analysisId, versionId);
  const { data, isPending, isError } = useGetAnalysisQuestionnaire(analysisId, versionId);
  const isEvaluatedFromData =
    data?.status === QuestionnaireStatus.evaluated ||
    data?.status === QuestionnaireStatus.questions_modified ||
    data?.status === QuestionnaireStatus.expired ||
    data?.status === QuestionnaireStatus.nsure_questions_changed;
  const [evaluatedOverride, setEvaluatedOverride] = useState<boolean | null>(null);
  const isEvaluated = evaluatedOverride !== null ? evaluatedOverride : isEvaluatedFromData;
  const [showDisabledForm, setShowDisabledForm] = useState(false);
  const [showEditableForm, setShowEditableForm] = useState(false);
  const isErrorNSurePlans = (data?.nsurePlans?.length ?? 0) === 0 && (data?.goals?.length ?? 0) > 0;

  const { isInvestmentStatesPending, isInvestmentStatesError } = useLockedInvestmentCaseStatus(analysisId, versionId);

  if (isPending || isAnalysisPending || isInvestmentStatesPending) {
    return <Loader />;
  }

  if (isError || isAnalysisError || isInvestmentStatesError || !analysisData || !data || isErrorNSurePlans) {
    return (
      <Empty
        title={t(Resources.Questionnaire.Error.Title)}
        text={t(Resources.Questionnaire.Error.Text, { email: ANALYZER_EMAIL })}
        icon={<ErrorOutlineIcon sx={{ fontSize: "40px", color: "grey.500" }} />}
      />
    );
  }

  if (data.goals.length === 0) {
    return (
      <Empty
        title={t(Resources.Questionnaire.NoGoals.Title)}
        text={t(Resources.Questionnaire.NoGoals.Text)}
        icon={<IconBadge color="grey" icon={<ErrorOutlineIcon sx={{ fontSize: 35 }} />} />}
      />
    );
  }

  if (readOnly && data.status === QuestionnaireStatus.new) {
    return (
      <Empty
        title={t(Resources.Questionnaire.ReadOnly.Title)}
        text={t(Resources.Questionnaire.ReadOnly.Text)}
        icon={<IconBadge color="grey" icon={<ErrorOutlineIcon sx={{ fontSize: 35 }} />} />}
      />
    );
  }

  const requiresChangeReason = isEvaluatedFromData && !isExpiredStatus(data.status);

  const questionnaire = data.questionnaire;
  if (!questionnaire) {
    return (
      <Empty
        title={t(Resources.Questionnaire.Error.Title)}
        text={t(Resources.Questionnaire.Error.Text, { email: ANALYZER_EMAIL })}
        icon={<ErrorOutlineIcon sx={{ fontSize: "40px", color: "grey.500" }} />}
      />
    );
  }

  const allQuestions = (questionnaire.questionnaireQuestions ?? []).filter((q) => q.subType !== "PayoutOnly");
  const groups = questionnaire.questionnaireGroups ?? [];

  const initialAnswerItems = (data?.questionnaireAnswers ?? []).flatMap((qa) => qa.questionnaireAnswerItems ?? []);

  return (
    <QuestionnaireProvider
      goalsData={data?.goals ?? []}
      allQuestions={allQuestions}
      groups={groups}
      investmentPlans={data?.nsurePlans ?? []}
      organizedBy={analysisData.organizedBy}
      isReadOnly={readOnly || (showDisabledForm && !showEditableForm)}
    >
      <Stack spacing={Spacing.XXXL} paddingBottom={Spacing.XXXL}>
        <Flex width={"100%"} justifyContent={"space-between"} alignItems={"center"} pr={Spacing.L}>
          <Box>
            <Typography variant="h3">{t(Resources.Questionnaire.Title)}</Typography>
            <Typography variant="body2" color="secondary">
              {t(Resources.Questionnaire.Subtitle)}
            </Typography>
          </Box>
          {isEvaluated && showDisabledForm && !showEditableForm && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setShowDisabledForm(false)}
              endAdornment={<KeyboardArrowRightIcon fontSize="small" sx={{ position: "relative", top: "-1px" }} />}
            >
              {t(Resources.Questionnaire.Evaluation.ShowEvaluation)}
            </Button>
          )}
          {!isEvaluated && data.status === QuestionnaireStatus.new && <QuestionnaireMoreActions />}
        </Flex>

        {isEvaluated ? (
          showEditableForm ? (
            <QuestionnaireForm
              initialAnswerItems={initialAnswerItems}
              onEvaluated={() => setShowEditableForm(false)}
              requiresChangeReason={requiresChangeReason}
              hasUnevaluatedChanges={data.hasUnevaluatedChanges}
            />
          ) : showDisabledForm ? (
            <QuestionnaireForm initialAnswerItems={initialAnswerItems} onEvaluated={() => setShowDisabledForm(false)} />
          ) : (
            <QuestionnaireEvaluation
              status={data.status}
              onReEvaluate={() => {
                setEvaluatedOverride(false);
                setShowDisabledForm(false);
              }}
              onShowQuestionnaire={() => setShowDisabledForm(true)}
              onChangeAnswers={() => {
                setShowDisabledForm(false);
                setShowEditableForm(true);
              }}
            />
          )
        ) : (
          <QuestionnaireForm
            initialAnswerItems={initialAnswerItems}
            onEvaluated={() => setEvaluatedOverride(true)}
            requiresChangeReason={requiresChangeReason}
            hasUnevaluatedChanges={data.hasUnevaluatedChanges}
          />
        )}
      </Stack>
    </QuestionnaireProvider>
  );
};
